// API client for the patient portal's sidebar (reports + AI assistant).
//
// Every call carries the Supabase access token as a Bearer header. The backend
// (`api/patient_reports.resolve_patient_caller`) verifies that token against
// Supabase and derives the user id from it — deliberately NOT from the request
// body, because these endpoints write files into a chart. Passing a user id
// would let anyone file a report onto any patient's chart.
import { getSupabase } from "../supabaseClient";
import { getBackendURL } from "../env";

async function authHeader() {
  const sb = await getSupabase();
  const {
    data: { session },
  } = await sb.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("You're signed out. Please sign in again.");
  return `Bearer ${token}`;
}

// The backend answers errors as JSON `{error, reason}`; `reason` is written for
// the patient to read, so prefer it and fall back to something non-technical.
async function readError(res, fallback) {
  let body = null;
  try {
    body = await res.json();
  } catch (_) {
    /* non-JSON error body (proxy/HTML) — fall through to the generic text */
  }
  const message = body?.reason || body?.error || fallback;
  const err = new Error(message);
  err.code = body?.error || null;
  err.status = res.status;
  return err;
}

/** Every report this patient can see, newest first. */
export async function fetchPatientReports(appointmentId) {
  const qs = appointmentId
    ? `?appointment_id=${encodeURIComponent(appointmentId)}`
    : "";
  const res = await fetch(`${getBackendURL()}/api/patient/reports/${qs}`, {
    headers: { Authorization: await authHeader() },
  });
  if (!res.ok) throw await readError(res, "We couldn't load your reports.");
  const data = await res.json();
  return Array.isArray(data.files) ? data.files : [];
}

/**
 * Upload one report. The backend dual-files it to the patient's next
 * appointment folder AND their chart folder, and returns which copies it made
 * so the caller can tell the patient where it went.
 */
export async function uploadPatientReport(file) {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${getBackendURL()}/api/patient/reports/upload/`, {
    method: "POST",
    headers: { Authorization: await authHeader() },
    body: form,
  });
  if (!res.ok) throw await readError(res, "That upload didn't go through.");
  return res.json();
}

/**
 * Ask the patient-facing assistant a question.
 *
 * The backend runs this over the patient-safe AI SUMMARIES (never the raw note
 * or raw recordings) and answers with a patient-facing system prompt.
 */
export async function queryPatientAssistant({
  query,
  appointmentId,
  selectedSources,
  history,
}) {
  const res = await fetch(`${getBackendURL()}/api/patient/ai-assistant/query/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: await authHeader(),
    },
    body: JSON.stringify({
      query,
      appointmentId: appointmentId || null,
      selectedSources,
      history,
    }),
  });
  if (!res.ok) throw await readError(res, "The assistant couldn't answer that.");
  return res.json();
}

export function formatBytes(n) {
  if (!Number.isFinite(n)) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function fileExtOf(name) {
  const i = (name || "").lastIndexOf(".");
  return i === -1 ? "" : name.substring(i + 1).toLowerCase();
}
